/**
 * HI-1430 — Energy single-question-per-page funnel slugs, single source of truth.
 *
 * Lives in shared/ because nuxt.config.ts reads it to constrain the variant's
 * dynamic route (`pages:extend`), and it must not drag dayjs — which
 * app/constants/funnel/energy-sqpp.js does — into the config load.
 *
 * Re-exported as SQPP_SLUGS from app/constants/funnel/energy-sqpp so app code
 * keeps importing from there. The order here IS the funnel order; the flow
 * table asserts the two agree.
 */
export const ENERGY_SQPP_SLUGS = [
  'what-to-compare',
  'home-or-business',
  'moving',
  'moving-date',
  'own-or-rent',
  'address',
  'postcode',
  'business-name',
  'usage',
  'electricity-retailer',
  'gas-retailer',
  'life-support',
  'solar',
  'contact-details'
] as const

/** Route-regex alternation. A whitelist means an unknown slug 404s at the router. */
export const ENERGY_SQPP_SLUG_PATTERN = ENERGY_SQPP_SLUGS.join('|')

/**
 * The scanned path of the variant page file, which `pages:extend` rewrites to
 * the flat `/energy/quote/form/<slug>` space so the `sqpp/` URL never goes live.
 */
export const ENERGY_SQPP_SCANNED_PATH = '/energy/quote/form/sqpp/:slug()'

/** The control funnel's scanned dynamic path, constrained to `stepN` only. */
export const ENERGY_CONTROL_SCANNED_PATH = '/energy/quote/form/:step()'
export const ENERGY_CONTROL_CONSTRAINED_PATH = '/energy/quote/form/:step(step[0-9]+)'

interface ScannedPage {
  path: string
}

/**
 * Rewrite the Energy funnel routes in Nuxt's `pages:extend` hook.
 *
 * MUTATES the scanned routes rather than pushing new ones, so exactly one route
 * exists per URL space and the `sqpp/` path never goes live:
 *
 *   - the variant page moves from `/energy/quote/form/sqpp/:slug()` to the flat
 *     `/energy/quote/form/:slug(<alternation>)` — a whitelist, so an unknown
 *     slug 404s at the router instead of rendering an empty funnel
 *   - the control funnel is constrained to `:step(step[0-9]+)` so it stops
 *     shadowing those slugs. `/energy/quote/form/step9` still matches and still
 *     hits [step].vue's own createError(404).
 *
 * Exported (rather than inlined in nuxt.config.ts) so the resulting route table
 * is unit-testable — the alternative is asserting on config source text.
 */
export function applyEnergySqppRoutes<T extends ScannedPage> (pages: T[]): T[] {
  const variantRoute = pages.find(page => page.path === ENERGY_SQPP_SCANNED_PATH)
  if (variantRoute) {
    variantRoute.path = `/energy/quote/form/:slug(${ENERGY_SQPP_SLUG_PATTERN})`
  }

  const controlRoute = pages.find(page => page.path === ENERGY_CONTROL_SCANNED_PATH)
  if (controlRoute) {
    controlRoute.path = ENERGY_CONTROL_CONSTRAINED_PATH
  }

  return pages
}
